import { format } from 'date-fns';
import React, { useState } from 'react';
import { View } from 'react-native';
import styled from 'styled-components/native';
import Button from '../../../components/Button';
import Spacer from '../../../components/Spacer';

import { H3, P, B } from '../../../components/Typography';
import StudyDynamicCircle from '../StudyDynamicCircle';
import Colors from '../../../constants/Colors';
import Card from '../../../components/Card';
import { Ionicons } from '@expo/vector-icons';
import PdfModal from '../../../components/PdfModal';
import api from '../../../api/api';
import { useUserContext } from '../../../context/UserContext';
import { formatStudyLength } from '../../../common/formatStudyLength';
import elementIDs from '../../../../elementIDs';

const StyledOverviewHeaderCard = styled(Card)`
  padding: 37px 16px 25px 16px;
`;
const StyledEditStudyContainer = styled.View`
  align-items: flex-end;
  margin-top: -24px;
  width: 100%;
`;

const StyledStatusContainer = styled.View`
  flex-grow: 1;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
`;

const StyledStatusTextContainer = styled.View`
  flex: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  margin: 0px 0px 12px 24px;
`;

const StyledStatusTextRow = styled.View`
  flex-direction: row;
  justify-content: space-between;
`;

const StyledStatusCircle = styled.View`
  height: 100px;
  width: 100px;
  border: 1px;
  border-radius: 80px;
  justify-content: center;
  align-items: center;
  border-color: ${Colors.studyScreenStatusCircleBorder};
`;

export interface StudyOverviewHeaderProps {
  status: string;
  studyName: string;
  startDate?: Date;
  endDate?: Date;
  studyLength?: number;
  isCompletedStudy: boolean;
  display?: 'daysLeft' | 'daysLogged';
  description?: string;
  // studyType?: 'Consumer' | 'Sponsor';
  organizationName?: string;
  studyParticipantId?: number;
  isEnableShareResult?: boolean;
  onEdit?: () => void;
}

const StudyOverviewHeader: React.FC<StudyOverviewHeaderProps> = ({
  status,
  studyName,
  startDate,
  endDate,
  studyLength,
  isCompletedStudy,
  display,
  description,
  // studyType = 'Consumer',
  organizationName,
  studyParticipantId,
  isEnableShareResult,
  onEdit
}) => {
  const { accessToken } = useUserContext();
  const [showPDFModal, setShowPDFModal] = useState(false);

  return (
    <StyledOverviewHeaderCard>
      {(status === 'progress' || status === 'planning') && (
        <StyledEditStudyContainer>
          <Ionicons
            testID={elementIDs.settingsIconID}
            onPress={() => onEdit?.()}
            name='settings-outline'
            size={24}
            color={isCompletedStudy ? Colors.buttonOutlineDisabled : '#000'}
          />
        </StyledEditStudyContainer>
      )}
      <H3 testID={elementIDs.studyNameID} color='dark' padding={2}>
        {studyName}
      </H3>
      <P color='dark' testID={elementIDs.studyDurationID}>
        {formatStudyLength(studyLength || 0)} •{' '}
        {/* {studyType === 'Consumer'
          ? 'Self Study'
          : organizationName ?? 'Sponsored Study'} */}
        {organizationName ?? 'Sponsored Study'}
      </P>
      <Spacer size={4} />
      <StyledStatusContainer>
        <StyledStatusCircle testID={elementIDs.studyDaysLoggedID}>
          <StudyDynamicCircle
            display={display}
            statusType={status}
            textColor='dark'
            startDate={startDate}
            size={102}
            progressWidth={4}
            progressColor={Colors.studyScreenStatusCircleProgress}
            // use an abritrary length of study is completed
            studyLength={isCompletedStudy ? 1 : studyLength || 0}
            isCompletedStudy={isCompletedStudy}
          />
        </StyledStatusCircle>
        <StyledStatusTextContainer>
          <View>
            <StyledStatusTextRow testID={elementIDs.studyStartDateID}>
              <B color='dark'>Start Date</B>
              <P color='dark'>
                {startDate ? format(startDate, 'MMM d, yyyy') : 'N/A'}
              </P>
            </StyledStatusTextRow>
            <Spacer size={0.25} />
            <StyledStatusTextRow testID={elementIDs.studyEndDateID}>
              <B color='dark'>End Date</B>
              <P color='dark'>
                {endDate ? format(endDate, 'MMM d, yyyy') : 'N/A'}
              </P>
            </StyledStatusTextRow>
          </View>
        </StyledStatusTextContainer>
      </StyledStatusContainer>
      {!!description && (
        <>
          <Spacer size={3} />
          <B testID={elementIDs.studySummaryID} color='dark'>
            Study Summary
          </B>
          <Spacer size={2} />
          <P color='dark' testID={elementIDs.studyDescriptionID}>
            {description}
          </P>
        </>
      )}
      <Spacer size={3} />
      {!!studyParticipantId && isEnableShareResult && (
        <Button color='secondary' onPress={() => setShowPDFModal(true)}>
          Share My Results
        </Button>
      )}
      {showPDFModal && (
        <PdfModal
          title={'Export'}
          url={`${api.getBaseUrl()}study-participants/${studyParticipantId}/export-report-pdf?timezoneId=${
            Intl.DateTimeFormat().resolvedOptions().timeZone
          }&status=${isCompletedStudy ? 'Completed' : 'In Progress'}`}
          headers={{
            [api.environment.accessTokenKey]: `Bearer ${accessToken}`
          }}
          onRequestClose={() => setShowPDFModal(false)}
        />
      )}
    </StyledOverviewHeaderCard>
  );
};

export default StudyOverviewHeader;
